import {
  memo,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flip, hide, offset, shift, useFloating } from '@floating-ui/react-dom';

import { ChessBoard } from '@exports/ChessBoard';
import { formatDecimalValuation } from '@libs/chess/formatValuation';
import { IPgnMove } from '@libs/chess/pgn';
import { SquareName } from '@libs/chess/types';
import { cx } from '@libs/classnames';
import { usePrev } from '@libs/hooks/usePrev';
import { AnimatedNode } from '@ui/components/Animation';
import { SimpleToggle } from '@ui/components/SimpleToggle';

import styles from './Valuator.module.css';

export type Props = {
  className?: string;
  title: string;
  depth?: number;
  enabled?: boolean;
  expanded?: boolean;
  thinking?: boolean;
  variants?: IVariant[];
  pending?: boolean;
  preview?: IPreview;
  isError?: boolean;
  onToggle?: (enabled: boolean) => void;
  onExpand?: (expanded: boolean) => void;
  onMoveHover?: (m: IPgnMove, v: IVariant) => void;
  onMoveClick?: (m: IPgnMove, v: IVariant) => void;
};

// HACK: workaround для бага https://bugs.webkit.org/show_bug.cgi?id=308937
// Safari on iOS fires "pointerenter" with mouse pointerType on elements being removed.
// Кратко: а) сафари генерирует mouseEnter при клике б) сафари генерирует pointerEnter для
// мыши на элементах: которые появляются на месте кликнутого и удалённого
let TOUCH_DETECTED = false;

export interface IPreview {
  fen: string;
  from?: SquareName;
  to?: SquareName;
  winner?: null | 'draw' | 'white' | 'black';
}

export interface IVariant {
  depth: number;
  valuation: number;
  type: 'cp' | 'mate';
  moves: IPgnMove[];
}

export function Valuator({
  className,
  title,
  depth,
  enabled,
  expanded,
  thinking,
  variants,
  pending,
  isError,
  preview,
  onToggle,
  onExpand,
  onMoveHover,
  onMoveClick,
}: Props) {
  const variantsToShow = variants?.length ? (expanded ? variants : [variants[0]!]) : undefined;

  const previewMoveRef = useRef<HTMLSpanElement>(null);
  const [el, setEl] = useState(previewMoveRef.current);

  const handleMoveClick = useCallback(
    (m: IPgnMove, v: IVariant) => {
      onMoveClick?.(m, v);
      setEl(null);
    },
    [onMoveClick],
  );
  const handleMoveMouseEnter = useCallback(
    (m: IPgnMove, v: IVariant) => {
      onMoveHover?.(m, v);
      setEl(previewMoveRef.current);
    },
    [onMoveHover],
  );
  const handleVariantMouseLeave = useCallback(() => {
    setEl(null);
  }, []);

  const mainLineValue = thinking ? undefined : variants?.[0];
  const mainLine = useMemo(() => {
    if (mainLineValue) {
      const formatted = formatDecimalValuation(mainLineValue.type, mainLineValue.valuation, 1);
      if (formatted === '#0') {
        return <>Мат</>;
      }
      let signLength = 1;
      let sign = formatted[0];
      if (sign === '#' && formatted[1] === '-') {
        sign = '#−';
        signLength = 2;
      }
      const rest = formatted.substring(signLength);
      return (
        <>
          <span>{sign}</span>
          {rest}
        </>
      );
    }
    return undefined;
  }, [mainLineValue]);

  return (
    <div
      className={cx(styles.valuator, styles.theme, className, {
        [styles.is_expanded]: expanded,
        [styles.allow_hover]: !TOUCH_DETECTED, // sic! константа не реактивная, но мы знаем что её изменение всегда связано с перерисовкой
      })}
    >
      <div className={styles.header}>
        <div className={styles.header__title}>{title}</div>
        <div className={styles.header__indicator}>
          {enabled && !thinking && mainLine && (
            <span className={styles.mainline_value}>{mainLine}</span>
          )}
          {enabled && (thinking || !mainLine) && (
            <span className={styles.mainline_thinking}>
              <Loader />
            </span>
          )}
        </div>
        <div className={styles.header__controls}>
          {enabled && pending && <Loader />}
          {enabled && depth !== undefined && (depth > 0 || pending) && (
            <span className={styles.depth}>Глубина {depth.toString().padStart(2, '0')}</span>
          )}
          <SimpleToggle checked={enabled} color="green" onChange={onToggle} />
        </div>
      </div>
      {enabled && (
        <>
          <div className={styles.variants}>
            {thinking && (
              <>
                {expanded ? (
                  <>
                    <LineSkeleton />
                    <LineSkeleton />
                    <LineSkeleton />
                  </>
                ) : (
                  <LineSkeleton />
                )}
              </>
            )}
            {!thinking &&
              !isError &&
              variantsToShow?.map((v, i) => (
                <Variant
                  key={i}
                  variant={v}
                  pending={pending}
                  moveHoverRef={previewMoveRef}
                  onMoveMouseEnter={handleMoveMouseEnter}
                  onVariantMouseLeave={handleVariantMouseLeave}
                  onMoveClick={handleMoveClick}
                />
              ))}
            {!thinking && !pending && !isError && !variantsToShow && (
              <EmptyVariantsBadge>нет ходов</EmptyVariantsBadge>
            )}
            {!thinking && isError && <EmptyVariantsBadge>ошибка</EmptyVariantsBadge>}
          </div>
          <button className={styles.expand_button} onClick={() => onExpand?.(!expanded)}>
            <span />
          </button>
        </>
      )}

      <AnimatedNode className={styles.preview}>
        {preview && el && <Preview preview={preview} el={el} />}
      </AnimatedNode>
    </div>
  );
}

const Variant = memo(function Variant({
  variant,
  pending,
  moveHoverRef,
  onMoveMouseEnter,
  onVariantMouseLeave,
  onMoveClick,
}: {
  variant: IVariant;
  pending?: boolean;
  moveHoverRef: MutableRefObject<HTMLSpanElement | null>;
  onMoveMouseEnter?: (m: IPgnMove, v: IVariant) => void;
  onVariantMouseLeave?: () => void;
  onMoveClick?: (m: IPgnMove, v: IVariant) => void;
}) {
  const { type, moves, valuation } = variant;
  const first = moves[0];
  const hasMoves = Boolean(first);

  if (!hasMoves && !pending) {
    return <EmptyVariantsBadge>нет ходов</EmptyVariantsBadge>;
  }

  if (pending && !hasMoves) return <LineSkeleton />;

  return (
    <div className={styles.variant} onPointerLeave={onVariantMouseLeave}>
      <div className={styles.variant__moves}>
        {hasMoves && (
          <span className={styles.variant__valuation}>
            {formatDecimalValuation(type, valuation, 1)}
          </span>
        )}

        {first?.color === 'black' && <MoveStub moveFull={first.moveFull} />}
        {moves.map((m) => (
          <Move
            key={`${m.moveFull}-${m.color}-${m.san}`}
            move={m}
            variant={variant}
            moveHoverRef={moveHoverRef}
            onMouseEnter={onMoveMouseEnter}
            onClick={onMoveClick}
          />
        ))}
      </div>
    </div>
  );
});

function Move({
  move,
  variant,
  moveHoverRef,
  onMouseEnter,
  onClick,
}: {
  move: IPgnMove;
  variant: IVariant;
  moveHoverRef: MutableRefObject<HTMLSpanElement | null>;
  onMouseEnter?: (m: IPgnMove, v: IVariant) => void;
  onClick?: (m: IPgnMove, v: IVariant) => void;
}) {
  const isWhite = move.color === 'white';
  const ref = useRef(null);
  return (
    <>
      <span className={styles.move}>
        {isWhite && <span className={styles.move__number}>{move.moveFull}.&nbsp;</span>}
        <span
          ref={ref}
          className={styles.move__san}
          onPointerEnter={(e) => {
            if (e.pointerType === 'touch' || TOUCH_DETECTED) {
              e.preventDefault();
              TOUCH_DETECTED = true;
              return;
            }

            moveHoverRef.current = ref.current;
            onMouseEnter?.(move, variant);
          }}
          onClick={() => onClick?.(move, variant)}
        >
          <span>{move.san}</span>
        </span>
      </span>{' '}
    </>
  );
}

function MoveStub({ moveFull }: { moveFull: number }) {
  return (
    <>
      <span className={styles.move}>
        <span className={styles.move__number}>{moveFull}&hellip;&nbsp;</span>
      </span>
      &nbsp;
    </>
  );
}

function Loader({ className }: { className?: string }) {
  return <div className={cx(styles.loader, className)}></div>;
}

const Preview = memo(function Preview({
  preview,
  el,
}: {
  preview: IPreview;
  el: HTMLSpanElement | null;
}) {
  const floating = useFloating({
    placement: 'bottom',
    transform: true,
    // whileElementsMounted: autoUpdate,
    middleware: [offset({ mainAxis: 14 }), flip(), shift(), hide()],
  });

  useEffect(() => {
    if (el) {
      floating.refs.setReference(el);
    }
  }, [floating.refs, el]);

  const style = floating.floatingStyles;
  const positioned = floating.isPositioned;
  const hidden = floating.middlewareData.hide?.referenceHidden;
  // HACK:
  const badPositioning = !style.transform || style.transform === 'translate(0px, 0px)';
  const prevPositioned = usePrev(positioned);
  const prevHidden = usePrev(hidden);
  const noTransition = !positioned || !prevPositioned || hidden || prevHidden || badPositioning;

  if (!el) {
    return null;
  }

  return (
    <div
      className={styles.preview__container}
      style={{
        ...style,
        visibility: positioned && !hidden && !badPositioning ? 'visible' : 'hidden',
        transition: noTransition ? 'none' : undefined,
      }}
      ref={floating.refs.setFloating}
    >
      <Board preview={preview} />
    </div>
  );
});

const Board = memo(function Board({ preview }: { preview: IPreview }) {
  return (
    <ChessBoard
      orientation="white"
      noShadow
      position={preview.fen}
      selectedFrom={preview.from}
      selectedTo={preview.to}
      winner={preview.winner ?? undefined}
    />
  );
});

function EmptyVariantsBadge({ children }: { children: ReactNode }) {
  return (
    <div className={cx(styles.variant, styles.is_no_line)}>
      <div className={styles.variant__moves}>{children}</div>
    </div>
  );
}

function LineSkeleton() {
  return (
    <div className={styles.variant}>
      <div className={styles.line_skeleton}>
        <div className={styles.line_skeleton__valuation} />
        <span className={styles.line_skeleton__line} />
        <span className={styles.line_skeleton__line} />
        <span className={styles.line_skeleton__line} />
      </div>
    </div>
  );
}
